/**
 * Template file import/export operations
 * Supports encrypted and plain JSON formats
 */

import { serializeTemplate } from "../core/templateSerialization.js";
import { WORKSPACE_CONFIG } from "../constants.js";
import logger from "../../utils/logger";
import {
  encryptDesktop,
  decryptDesktop,
  checkEncryption,
  isPlainJson,
} from "../utils/encryption.js";

/**
 * Export template to file (encrypted or plain)
 * @param {Object} chart - The chart to export as template
 * @param {string} filename - Optional filename (defaults to chart-based name)
 * @param {string} password - Optional password (blank = obfuscation only)
 * @returns {Promise<{success: boolean, encrypted: boolean, passwordProtected: boolean}>}
 */
export const exportTemplate = async (chart, filename = null, password = "") => {
  try {
    if (!chart) {
      throw new Error("Gráfico é obrigatório para exportar template");
    }

    // Serialize the template
    const templateData = serializeTemplate(chart);
    let content = JSON.stringify(templateData, null, 2);

    let encrypted = false;
    let passwordProtected = false;

    // Always encrypt (with or without password)
    content = await encryptDesktop(content, password);
    encrypted = true;
    passwordProtected = password && password.trim().length > 0;

    // Create blob and download
    const blob = new Blob([content], { type: WORKSPACE_CONFIG.FILE_MIME_TYPE });
    const url = URL.createObjectURL(blob);

    // Generate filename if not provided
    const defaultFilename = `template-${chart.symbol || "chart"}-${Date.now()}${WORKSPACE_CONFIG.TEMPLATE_EXTENSION}`;
    const finalFilename = filename || defaultFilename;

    // Create download link
    const link = document.createElement("a");
    link.href = url;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);

    logger.workspace.success("Template exportado com sucesso", {
      filename: finalFilename,
      chartType: templateData.chartType,
      indicatorsCount: templateData.indicators?.length || 0,
      encrypted,
      passwordProtected,
    });

    return { success: true, encrypted, passwordProtected };
  } catch (error) {
    console.error("Failed to export template:", error);
    logger.workspace.error("Falha ao exportar template", {
      error: error.message,
    });
    throw error;
  }
};

/**
 * Import template from file (supports encrypted and plain JSON)
 * @param {Function} applyTemplate - Function to apply the template (chartStore, chartId, templateData)
 * @param {string} chartId - Target chart ID to apply template to
 * @param {string} password - Optional password for encrypted files
 * @returns {Promise<{success: boolean, encrypted: boolean, passwordRequired: boolean}>}
 */
export const importTemplate = async (applyTemplate, chartId, password = "") => {
  try {
    if (!chartId) {
      throw new Error("ID do gráfico é obrigatório");
    }

    // Create file input
    const input = document.createElement("input");
    input.type = "file";
    input.accept = WORKSPACE_CONFIG.FILE_ACCEPT;

    return new Promise((resolve, reject) => {
      input.onchange = async (event) => {
        const file = event.target.files[0];
        if (!file) {
          resolve({ success: false, cancelled: true });
          return;
        }

        try {
          const reader = new FileReader();

          reader.onload = async (e) => {
            try {
              const content = e.target.result;

              // Check if encrypted
              const encryptionInfo = checkEncryption(content);
              const isEncryptedFile = encryptionInfo !== null;

              let templateData;

              if (isEncryptedFile) {
                console.log(
                  "Template file is encrypted, password required:",
                  encryptionInfo.passwordRequired,
                );

                // If password is required but not provided, throw error with file content
                if (
                  encryptionInfo.passwordRequired &&
                  (!password || password.trim().length === 0)
                ) {
                  const error = new Error("PASSWORD_REQUIRED");
                  error.fileContent = content; // Attach file content for retry
                  throw error;
                }

                // Decrypt
                const decryptedJson = await decryptDesktop(content, password);
                templateData = JSON.parse(decryptedJson);
              } else if (isPlainJson(content)) {
                // Legacy plain JSON format
                console.log("Template file is plain JSON (legacy format)");
                templateData = JSON.parse(content);
              } else {
                throw new Error("Formato de arquivo inválido");
              }

              // Validate template data
              if (!templateData.chartType && !templateData.indicators) {
                throw new Error("Arquivo não é um template válido");
              }

              // Apply the template using the provided function
              const success = await applyTemplate(templateData, chartId);

              if (success) {
                logger.workspace.success("Template importado com sucesso", {
                  filename: file.name,
                  chartType: templateData.chartType,
                  indicatorsCount: templateData.indicators?.length || 0,
                  encrypted: isEncryptedFile,
                  passwordProtected: encryptionInfo?.passwordRequired || false,
                });
                resolve({
                  success: true,
                  encrypted: isEncryptedFile,
                  passwordRequired: encryptionInfo?.passwordRequired || false,
                });
              } else {
                throw new Error("Falha ao aplicar template");
              }
            } catch (error) {
              console.error("Failed to parse or apply template:", error);
              logger.workspace.error("Falha ao processar template", {
                error: error.message,
                filename: file.name,
              });
              reject(error);
            }
          };

          reader.onerror = () => {
            const error = new Error("Falha ao ler o arquivo");
            logger.workspace.error("Erro ao ler arquivo", {
              error: error.message,
              filename: file.name,
            });
            reject(error);
          };

          reader.readAsText(file);
        } catch (error) {
          reject(error);
        }
      };

      input.oncancel = () => {
        resolve({ success: false, cancelled: true });
      };

      input.click();
    });
  } catch (error) {
    console.error("Failed to import template:", error);
    throw error;
  }
};

/**
 * Copy template to clipboard (encrypted)
 * @param {Object} chart - The chart to copy as template
 * @param {string} password - Optional password (blank = obfuscation only)
 * @returns {Promise<boolean>}
 */
export const copyTemplateToClipboard = async (chart, password = "") => {
  try {
    if (!chart) {
      throw new Error("Gráfico é obrigatório");
    }

    const templateData = serializeTemplate(chart);

    // Always encrypt for clipboard
    const content = await encryptDesktop(
      JSON.stringify(templateData, null, 2),
      password,
    );

    await navigator.clipboard.writeText(content);

    logger.workspace.success("Template copiado para área de transferência", {
      chartType: templateData.chartType,
      indicatorsCount: templateData.indicators?.length || 0,
      encrypted: true,
      passwordProtected: password && password.trim().length > 0,
    });

    return true;
  } catch (error) {
    console.error("Failed to copy template to clipboard:", error);
    logger.workspace.error("Falha ao copiar template", {
      error: error.message,
    });
    throw error;
  }
};
